<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { useIntervalFn } from '@vueuse/core'

import { LocationPoint } from '@/types/company'
import { DashboardWidget, WidgetType } from '@/types/dashboard'
import { Marker, ResourceResponse } from '@/types/general'
import { smallDotSvg } from '@/utils/maps'
import { useOngoingReportStore } from '@/stores/ongoing-driver-report-store'
import { useOngoingReportWidgetData } from '@/hooks/driver-reports/use-ongoing-driver-report-widget-data'

import MyButton from '@/components/my-components/MyButton.vue'
import MyWidget from '@/components/dashboard/widgets/MyWidget.vue'
import MapComponent from '@/components/MapComponent.vue'

interface Props {
    widget: DashboardWidget<WidgetType.DriverReportsOngoingReportMap>
    preview?: boolean
}

const props = defineProps<Props>()

const { t } = useI18n()
const ongoingReportStore = useOngoingReportStore()
const ongoingReportData = useOngoingReportWidgetData()

const reportTrackingEvents = ref<LocationPoint[]>([])

const markers = computed<Marker[]>(() => {
    if (ongoingReportStore.selectedReportId) {
        const reportIndex = ongoingReportData.reports.value.findIndex(
            (r) => r.id === ongoingReportStore.selectedReportId,
        )
        if (reportIndex === -1) return []
        const trackingPoints = reportTrackingEvents.value.filter(
            (point) => point.latitude && point.longitude,
        )
        const label = reportIndex + 1
        const lastIndex = trackingPoints.length - 1

        return trackingPoints.map((trackingEvent, index) => {
            const isLastPoint = index === lastIndex
            return {
                location: trackingEvent,
                markerId: `tracking-${ongoingReportStore.selectedReportId}-${index}`,
                label: isLastPoint ? label.toString() : '',
                customSvg: isLastPoint ? markerSvg(label, true) : smallDotSvg,
            }
        })
    }

    return ongoingReportData.reports.value
        .map((report, index) => {
            const isHighlighted = ongoingReportStore.highlightedReportId === report.id

            return {
                location: report.latestLocation ?? { latitude: 0, longitude: 0 },
                label: (index + 1).toString(),
                markerId: report.id,
                customSvg: markerSvg(index + 1, isHighlighted),
            }
        })
        .filter((marker) => marker.location.longitude !== 0)
})

async function fetchReportTrackingEvents() {
    const reportId = ongoingReportStore.selectedReportId
    if (!reportId) return

    reportTrackingEvents.value = []

    const { data } = await axios.get<ResourceResponse<LocationPoint[]>>(
        window.route('tracking-events.for-report', { report: reportId }),
    )
    reportTrackingEvents.value = data.data
}

function markerSvg(label: number, highlighted: boolean): string {
    return `<svg viewBox="0 0 1024 1024" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="512" cy="512" r="400" fill="white" stroke="green" stroke-width="64"/>
        <text x="50%" y="50%" font-family="Arial, Helvetica, sans-serif" font-size="360" font-weight="bold" fill="${highlighted ? '#069468' : 'black'}" text-anchor="middle" dominant-baseline="central">${label}</text>
    </svg>`
}

function handleMarkerClick(reportId: string) {
    if (ongoingReportStore.selectedReportId === reportId) {
        ongoingReportStore.resetSelectedReport()
        return
    }
    const report = ongoingReportData.reports.value.find((r) => r.id === reportId)
    if (report) {
        ongoingReportStore.setSelectedReport(report)
    }
}

watch(() => props.widget.options, ongoingReportData.fetchOngoingReports)
watch(
    () => ongoingReportStore.selectedReportId,
    (reportId) => {
        if (!reportId) {
            reportTrackingEvents.value = []
            return
        }

        fetchReportTrackingEvents()
    },
)
useIntervalFn(() => fetchReportTrackingEvents(), 1_000 * 60)
</script>

<template>
    <MyWidget :widget="props.widget" :preview="props.preview">
        <MapComponent
            class="w-full h-full"
            :markers="markers"
            animated
            :connect-markers="ongoingReportStore.selectedReportId !== null"
            @marker-clicked="handleMarkerClick"
        />

        <div
            v-if="ongoingReportData.reports.value.length > 0"
            class="absolute text-xs right-0 top-0 bg-white dark:bg-primary-300 rounded-l-lg p-3"
        >
            <template v-for="(report, index) in ongoingReportData.reports.value" :key="report.id">
                <div
                    v-if="
                        !ongoingReportStore.selectedReportId ||
                        ongoingReportStore.selectedReportId === report.id
                    "
                    class="flex transition-colors"
                    :class="{
                        'text-green-500':
                            ongoingReportStore.highlightedReportId === report.id ||
                            ongoingReportStore.selectedReportId === report.id,
                    }"
                    @mouseenter="ongoingReportStore.setHighlightedReport(report)"
                    @mouseleave="ongoingReportStore.resetHighlightedReport()"
                    @click="handleMarkerClick(report.id)"
                >
                    <span class="font-semibold mr-1" v-text="index + 1" />
                    - {{ report.userName }}
                    <mdi:offline
                        v-if="report.latestLocation === null"
                        v-tooltip="t('noTrackingData')"
                        class="ml-1"
                    />

                    <MyButton
                        v-if="ongoingReportStore.selectedReportId === report.id"
                        icon
                        size="micro"
                        scheme="default"
                        class="h-6 w-6 rounded-full"
                        @click.stop="ongoingReportStore.resetSelectedReport()"
                    >
                        <mdi:close class="h-4 w-4" />
                    </MyButton>
                </div>
            </template>
        </div>

        <div
            v-if="ongoingReportData.reports.value.length === 0 && !ongoingReportData.loading.value"
            class="absolute right-0 top-0 flex justify-center items-center w-full h-full text-white bg-black/80 text-2xl uppercase"
            v-text="t('noOngoingReports')"
        />
    </MyWidget>
</template>
